import type { AnalyticsEvent, AnalyticsProps } from "./posthog";

/**
 * OpenAI Measurement Pixel: ad attribution for ChatGPT Ads campaigns.
 *
 * The base code is injected after cookie consent by
 * shared/consent/ad-pixels-loader.ts, next to Meta and TikTok. This module
 * only translates our funnel events into OpenAI standard events, and is
 * called from `track()` in ./posthog.ts like the Meta mapping.
 *
 * Same rule as Meta: activity bookings are pay on arrival, so they are a
 * `lead_created` with no amount. Stays are paid in full online (LiteAPI), so
 * they are an `order_created` with the charged amount. OpenAI wants amounts
 * as integers in the currency's minor unit (1850 EUR → 185000).
 *
 * No customer data is passed: no `user` object on init, and automatic
 * advanced matching is switched off on the data source in Ads Manager.
 */

type Oaiq = (...args: unknown[]) => void;

declare global {
  interface Window {
    oaiq?: Oaiq;
  }
}

type OpenAiContent = { id: string; name?: string; content_type: "product" };

type OpenAiEvent = {
  name: "contents_viewed" | "checkout_started" | "lead_created" | "order_created" | "custom";
  data: Record<string, unknown>;
  options?: { event_id?: string; custom_event_name?: string };
};

function str(v: AnalyticsProps[string]): string | undefined {
  return typeof v === "string" && v ? v : undefined;
}

function num(v: AnalyticsProps[string]): number | undefined {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

/** Major-unit amount → integer in the currency's minor unit, or undefined. */
export function toMinorUnits(value: number, currency: string): number | undefined {
  try {
    const digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    return Math.round(value * 10 ** digits);
  } catch {
    return undefined;
  }
}

function contents(props: AnalyticsProps): { contents?: OpenAiContent[] } {
  const id = str(props.activity_slug) ?? str(props.property_slug);
  if (!id) return {};
  const name = str(props.activity_title);
  return { contents: [{ id, ...(name ? { name } : {}), content_type: "product" }] };
}

/** Our funnel event → the OpenAI event it stands for, or null to skip. */
export function toOpenAiEvent(event: AnalyticsEvent, props: AnalyticsProps = {}): OpenAiEvent | null {
  switch (event) {
    case "activity_viewed":
      return { name: "contents_viewed", data: { type: "contents", ...contents(props) } };
    case "booking_details_started":
      return { name: "checkout_started", data: { type: "contents", ...contents(props) } };
    case "booking_completed": {
      const eventId = str(props.booking_id);
      const options = eventId ? { event_id: eventId } : undefined;
      if (props.payment === "online") {
        const value = num(props.total);
        const currency = str(props.currency)?.toUpperCase();
        const amount = value && currency ? toMinorUnits(value, currency) : undefined;
        return {
          name: "order_created",
          data: { type: "contents", ...contents(props), ...(amount ? { amount, currency } : {}) },
          options,
        };
      }
      return { name: "lead_created", data: { type: "customer_action" }, options };
    }
    case "whatsapp_clicked":
      return {
        name: "custom",
        data: { type: "custom" },
        options: { custom_event_name: "whatsapp_clicked" },
      };
    default:
      return null;
  }
}

/** Sends the OpenAI equivalent of a funnel event. No-op when the pixel is not loaded. */
export function forwardToOpenAiPixel(event: AnalyticsEvent, props?: AnalyticsProps): void {
  if (typeof window === "undefined" || typeof window.oaiq !== "function") return;
  const mapped = toOpenAiEvent(event, props);
  if (!mapped) return;
  // event_id dedupes refreshes of the thank-you page (OpenAI keeps the first).
  if (mapped.options) window.oaiq("measure", mapped.name, mapped.data, mapped.options);
  else window.oaiq("measure", mapped.name, mapped.data);
}
